import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { parseLifeBaseWorkbookRows } from "@/lib/lifeBaseWorkbook";
import { useLocation } from "wouter";
import {
  AlertTriangle,
  ArrowLeft,
  RefreshCcw,
  Search,
} from "lucide-react";
import SalviaLayout from "@/components/SalviaLayout";
import LifeBaseImportWizard from "@/components/LifeBaseImportWizard";
import LifeImportHistoryPanel from "@/components/LifeImportHistoryPanel";
import TrainingVideoPlayer from "@/components/TrainingVideoPlayer";
import { usePageTitle } from "@/hooks/usePageTitle";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  canManageLifeBaseGovernance,
  LIFE_BASE_GOVERNANCE_READONLY_MESSAGE,
} from "@shared/lifeBasePermissions";
import {
  getLifeBaseImportMaxRowsMessage,
  getLifeBaseRelationshipLabel,
  LIFE_BASE_IMPORT_MAX_ROWS,
  LIFE_BASE_MULTI_COMPANY_COLUMN_LABEL,
  LIFE_BASE_TEMPLATE_TYPES,
} from "@shared/lifeBaseCatalog";
import {
  getLifeBaseFriendlyErrorMessage,
  LIFE_BASE_ERROR_MESSAGES,
} from "@shared/lifeBaseUserFacingErrors";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  getConsolidatedStatusBadgeClass,
  getPendingStatusBadgeClass,
} from "@/lib/lifeBaseUi";

const FIELD_HELPERS: Record<string, string> = {
  acao: "Use exatamente ATIVAR ou INATIVAR; aliases como ATIVA e INATIVA não são aceitos.",
  cpf: "Informe somente números do CPF da pessoa.",
  nome: "Preencha o nome completo sem abreviações críticas.",
  data_nascimento:
    "Digite a data exatamente em DD/MM/AAAA; formatos automáticos do Excel, ISO ou variações fora desse padrão serão rejeitados.",
  plano: "Informe exatamente um plano elegível para a empresa resolvida.",
  genero:
    "Preencha com MASCULINO/FEMININO ou um equivalente aceito pelo validador.",
  email: "O e-mail é obrigatório para o processamento final reconciliado.",
  telefone:
    "Informe já no padrão 55 + DDD + número, com 13 dígitos e sem espaços ou caracteres especiais.",
  empresa_destino_cnpj:
    "No template multiempresa, cada linha deve apontar o CNPJ da empresa de destino.",
};

const EXCEL_REFERENCE_NOTE_FILLCOLOR = 'fillcolor="infoBackground [80]" strokecolor="none [81]"';
const EXCEL_REFERENCE_NOTE_FILL = '<v:fill color2="infoBackground [80]"/>';

async function buildExcelNoteStyledWorkbookBlob(workbook: XLSX.WorkBook) {
  const workbookBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const zip = await JSZip.loadAsync(workbookBuffer);
  const vmlPaths = Object.keys(zip.files).filter(filePath => /^xl\/drawings\/vmlDrawing\d+\.vml$/.test(filePath));

  await Promise.all(
    vmlPaths.map(async filePath => {
      const vmlFile = zip.file(filePath);
      const vmlContent = vmlFile ? await vmlFile.async("string") : null;
      if (!vmlContent) {
        return;
      }
      const patchedVmlContent = vmlContent
        .replace(/fillcolor="#ECFAD4" strokecolor="#edeaa1"/g, EXCEL_REFERENCE_NOTE_FILLCOLOR)
        .replace(/<v:fill color2="#BEFF82" type="gradient" angle="-180"><o:fill type="gradientUnscaled" v:ext="view"\/><\/v:fill>/g, EXCEL_REFERENCE_NOTE_FILL);
      zip.file(filePath, patchedVmlContent);
    })
  );

  return zip.generateAsync({ type: "blob" });
}

function downloadWorkbookBlob(blob: Blob, fileName: string) {
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(downloadUrl);
}

function getLifeBaseUiErrorMessage(
  error: unknown,
  options: {
    fallback?: string;
    preferredCategory?: "validation" | "processing" | "unavailable" | "unexpected";
    allowValidationDetails?: boolean;
  } = {},
) {
  return getLifeBaseFriendlyErrorMessage({
    error,
    fallback: options.fallback,
    preferredCategory: options.preferredCategory,
    allowValidationDetails: options.allowValidationDetails,
  });
}

function formatDateTime(value?: string | number | Date | null) {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("pt-BR");
}

function formatLifeBaseDate(value?: string | null) {
  if (!value) return "Não informada";
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    return String(value).split("-").reverse().join("/");
  }
  return String(value);
}

function normalizeLifeBaseCpf(value?: string | null) {
  return String(value ?? "").replace(/\D/g, "");
}

function getLifeBaseBeneficiaryTypeLabel(value?: string | null) {
  return value === "dependente" ? "Dependente" : "Titular";
}

function getScopedCompanyDisplayName(company?: {
  id?: number | null;
  name?: string | null;
  nomeFantasia?: string | null;
  razaoSocial?: string | null;
  cnpj?: string | null;
  channelId?: number | null;
  channelName?: string | null;
  channelRazaoSocial?: string | null;
  channelNomeFantasia?: string | null;
  status?: string | null;
  type?: string | null;
  clientType?: string | null;
} | null) {
  if (!company) {
    return null;
  }

  return company.nomeFantasia || company.razaoSocial || company.name || `Empresa #${company.id ?? "-"}`;
}

function getScopedChannelDisplayName(scope?: {
  channelId?: number | null;
  channelName?: string | null;
  channelRazaoSocial?: string | null;
  channelNomeFantasia?: string | null;
}): string {
  if (!scope) {
    return "Canal não informado";
  }

  return scope.channelNomeFantasia || scope.channelRazaoSocial || scope.channelName || `Canal #${scope.channelId ?? "-"}`;
}

function getDraftStatusLabel(status?: string | null, isDiscarded?: boolean) {
  if (isDiscarded) {
    return "Descartado";
  }

  switch (status) {
    case "previewed":
      return "Planilha conferida";
    case "processing":
      return "Enviando base";
    case "completed":
      return "Enviado";
    case "error":
      return "Concluído com erros";
    case "pending_structure":
      return "Estrutura pendente";
    default:
      return status || "-";
  }
}

function getRowStatusTone(status?: string | null) {
  switch (status) {
    case "approved":
    case "created":
    case "updated":
    case "inactivated":
    case "reactivated":
      return "default" as const;
    case "rejected":
    case "error":
      return "destructive" as const;
    default:
      return "secondary" as const;
  }
}

function getOperationalStatusTone(status?: string | null) {
  switch (status) {
    case "Concluído":
    case "Concluído com pendências":
      return "default" as const;
    case "Envio não confirmado":
    case "Erro no processamento":
    case "Importação com erro":
      return "destructive" as const;
    case "Aguardando confirmação de envio":
      return "secondary" as const;
    default:
      return "secondary" as const;
  }
}

function buildPaginationWindow(currentPage: number, totalPages: number) {
  if (totalPages <= 0) {
    return [] as number[];
  }

  const start = Math.max(1, currentPage - 2);
  const end = Math.min(totalPages, start + 4);
  const adjustedStart = Math.max(1, end - 4);

  return Array.from({ length: end - adjustedStart + 1 }, (_, index) => adjustedStart + index);
}

function renderLifeRecordsPagination({
  currentPage,
  totalPages,
  isFetching,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  isFetching: boolean;
  onPageChange: (page: number) => void;
}) {
  if (totalPages <= 1) {
    return null;
  }

  const paginationWindow = buildPaginationWindow(currentPage, totalPages);

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Button
        variant="outline"
        size="sm"
        disabled={isFetching || currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        Anterior
      </Button>
      {paginationWindow.map(pageNumber => (
        <Button
          key={pageNumber}
          variant={pageNumber === currentPage ? "default" : "outline"}
          size="sm"
          disabled={isFetching}
          onClick={() => onPageChange(pageNumber)}
        >
          {pageNumber}
        </Button>
      ))}
      <Button
        variant="outline"
        size="sm"
        disabled={isFetching || currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Próxima
      </Button>
    </div>
  );
}

function readLifeBaseField(source: unknown, keys: string[]) {
  if (!source || typeof source !== "object") {
    return null;
  }

  const record = source as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
  }

  return null;
}

function getInspectedLifeBaseRowDetails(row: any) {
  const normalized =
    row?.normalizedDataJson && typeof row.normalizedDataJson === "object"
      ? row.normalizedDataJson
      : {};

  return {
    operation:
      row?.operationType ||
      readLifeBaseField(normalized, ["acao", "operationType"]),
    name: readLifeBaseField(normalized, ["nome", "name", "fullName"]),
    cpf: readLifeBaseField(normalized, ["cpf", "cpfNormalized"]),
    holderCpf: readLifeBaseField(normalized, [
      "cpfTitular",
      "cpf_titular",
      "holderCpfNormalized",
    ]),
    relationship: readLifeBaseField(normalized, [
      "relationshipLabel",
      "grauParentesco",
      "grau_parentesco",
      "relationshipType",
    ]),
    beneficiaryType: readLifeBaseField(normalized, [
      "tipoBeneficiario",
      "beneficiaryType",
    ]),
    plan: readLifeBaseField(normalized, ["plano", "planName"]),
    company: readLifeBaseField(normalized, [
      "empresa",
      "empresaDestino",
      "empresa_destino_cnpj",
      "companyName",
    ]),
    birthDate: readLifeBaseField(normalized, [
      "data_nascimento",
      "dataNascimento",
      "birthDate",
    ]),
    email: readLifeBaseField(normalized, ["email"]),
    phone: readLifeBaseField(normalized, ["telefone", "phone"]),
  };
}

export default function PortalPeopleBasePlaceholder() {
  usePageTitle("Base de Pessoas");
  const [location, setLocation] = useLocation();
  const utils = trpc.useUtils();
  const { user } = useAuth();
  const currentUserRole = typeof user?.role === "string" ? user.role : null;
  /* Compatibilidade textual com os guards do PRD:
  const isAdminRole = currentUserRole === "admin";
  const canUseMultiCompanyExperience =
    isAdminRole ||
  const isAdminSelectableMultiCompanyJourney =
    isMultiCompanyJourney && isAdminRole;
  */
  const isAdminRole = ["admin", "salvia_operation_admin"].includes(currentUserRole ?? "");
  const canManageGovernance = canManageLifeBaseGovernance(currentUserRole);

  const [companySearch, setCompanySearch] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [selectedTemplateType, setSelectedTemplateType] = useState<
    | (typeof LIFE_BASE_TEMPLATE_TYPES)[keyof typeof LIFE_BASE_TEMPLATE_TYPES]
    | null
  >(null);
  const [selectedImportFile, setSelectedImportFile] = useState<File | null>(
    null
  );
  const [selectedDraftId, setSelectedDraftId] = useState<number | null>(null);
  const [resolvedOperationalCompanyId, setResolvedOperationalCompanyId] = useState<number | null>(null);
  const [previewResult, setPreviewResult] = useState<any | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [draftResult, setDraftResult] = useState<any | null>(null);
  const [confirmResult, setConfirmResult] = useState<any | null>(null);
  const [processResult, setProcessResult] = useState<any | null>(null);
  const [importWizardSessionKey, setImportWizardSessionKey] = useState(0);
  const lastPreviewedFileSignatureRef = useRef<string | null>(null);
  const previousSubviewRef = useRef<"importacao" | "vidas" | "historico" | null>(null);
  const selectedSubview = useMemo<"importacao" | "vidas" | "historico">(
    () => {
      if (location.startsWith("/portal/base-de-pessoas/historico")) {
        return "historico";
      }
      if (location.startsWith("/portal/base-de-pessoas/vidas")) {
        return "vidas";
      }
      return "importacao";
    },
    [location]
  );
  const [lifeStatusFilter, setLifeStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [lifeSearch, setLifeSearch] = useState("");
  const [debouncedLifeSearch, setDebouncedLifeSearch] = useState("");
  const [selectedLifeRecordId, setSelectedLifeRecordId] = useState<
    string | number | null
  >(null);
  const [lifeChannelFilterId, setLifeChannelFilterId] = useState<number | null>(
    null
  );
  const [lifeCompanyFilterId, setLifeCompanyFilterId] = useState<number | null>(
    null
  );
  const [lifeRecordsPage, setLifeRecordsPage] = useState(1);
  const lifeRecordsPageSize = 50;

  const isLifeSubview = selectedSubview === "vidas";
  const selectedSubviewLabel =
    selectedSubview === "vidas"
      ? "Gerenciamento da base de vidas"
      : selectedSubview === "historico"
        ? "Histórico de importações"
        : "Importação de bases";
  const selectedSubviewDescription =
    selectedSubview === "vidas"
      ? "Consulte vidas ativas e inativas e ajuste o status da base vigente."
      : selectedSubview === "historico"
        ? "Acompanhe lotes enviados, retornos operacionais e detalhes do processamento por cliente dentro do seu escopo."
        : "Baixe o modelo, valide a planilha e acompanhe os lotes do contexto atual.";

  useEffect(() => {
    if (location === "/portal/base-de-pessoas") {
      setLocation("/portal/base-de-pessoas/importacao");
    }
  }, [location, setLocation]);

  useEffect(() => {
    const previousSubview = previousSubviewRef.current;

    if (selectedSubview === "importacao" && previousSubview && previousSubview !== "importacao") {
      setCompanySearch("");
      setSelectedCompanyId(null);
      setSelectedTemplateType(null);
      setSelectedImportFile(null);
      setSelectedDraftId(null);
      setResolvedOperationalCompanyId(null);
      setPreviewResult(null);
      setPreviewError(null);
      setDraftResult(null);
      setConfirmResult(null);
      setProcessResult(null);
      lastPreviewedFileSignatureRef.current = null;
      setImportWizardSessionKey(currentValue => currentValue + 1);
    }

    previousSubviewRef.current = selectedSubview;
  }, [selectedSubview]);

  const currentUserCompanyId = useMemo(() => {
    if (
      typeof user?.companyId === "number" &&
      Number.isFinite(user.companyId)
    ) {
      return user.companyId;
    }

    if (typeof user?.companyId === "string") {
      const parsedCompanyId = Number(user.companyId);
      return Number.isFinite(parsedCompanyId) ? parsedCompanyId : null;
    }

    return null;
  }, [user?.companyId]);

  const prefersScopedMultiCompanyContext =
    selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY;
  const activeContextCompanyId = currentUserRole === "client_rh"
    ? (currentUserCompanyId ?? selectedCompanyId)
    : prefersScopedMultiCompanyContext
      ? (currentUserCompanyId ?? selectedCompanyId)
      : selectedCompanyId;
  const effectiveCompanyIdForDraft = activeContextCompanyId ?? resolvedOperationalCompanyId;

  const snapshotQuery = trpc.lifeBase.getBootstrapSnapshot.useQuery(undefined, {
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const templateContractQuery = trpc.lifeBase.getTemplateContract.useQuery(
    undefined,
    {
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );
  const trainingVideosQuery = trpc.trainingVideos.getWorkspace.useQuery(undefined, {
    enabled: !!user,
    staleTime: 30_000,
  });
  const scopedCompanies = snapshotQuery.data?.scopedCompanies ?? [];
  const contextualTrainingVideos = trainingVideosQuery.data?.contextualPeopleBaseVideos ?? [];
  const preferredTrainingCompanyContext =
    selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY
      ? "group_company"
      : selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.SINGLE_COMPANY
        ? "single_company"
        : trainingVideosQuery.data?.companyContext ?? "single_company";
  const selectedContextualTrainingVideo =
    contextualTrainingVideos.find((video: any) => video.companyContext === preferredTrainingCompanyContext)
    ?? contextualTrainingVideos.find((video: any) => video.companyContext === "all")
    ?? contextualTrainingVideos[0]
    ?? null;
  const lifeChannelOptions = useMemo(() => {
    const options = new Map<number, { id: number; label: string }>();

    for (const company of scopedCompanies) {
      const channelId = Number(company.channelId);
      if (!Number.isFinite(channelId) || channelId <= 0) continue;
      if (!options.has(channelId)) {
        options.set(channelId, {
          id: channelId,
          label: getScopedChannelDisplayName(company),
        });
      }
    }

    return Array.from(options.values()).sort((left, right) => left.label.localeCompare(right.label, "pt-BR"));
  }, [scopedCompanies]);
  const lifeCompaniesAvailableForSelection = useMemo(
    () =>
      scopedCompanies.filter(company =>
        lifeChannelFilterId ? company.channelId === lifeChannelFilterId : true
      ),
    [lifeChannelFilterId, scopedCompanies]
  );
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedLifeSearch(lifeSearch);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [lifeSearch]);

  const lifeRecordsBaseInput = useMemo(
    () => ({
      companyId: lifeCompanyFilterId ?? undefined,
      channelId: isAdminRole ? lifeChannelFilterId ?? undefined : undefined,
      status: lifeStatusFilter,
      search: debouncedLifeSearch.trim() || undefined,
      page: lifeRecordsPage,
      pageSize: lifeRecordsPageSize,
    }),
    [
      isAdminRole,
      lifeChannelFilterId,
      lifeCompanyFilterId,
      lifeStatusFilter,
      debouncedLifeSearch,
      lifeRecordsPage,
    ]
  );

  const governanceQuery = trpc.lifeBase.listCompanyGovernance.useQuery(
    { companyId: activeContextCompanyId ?? 0 },
    {
      enabled: !!activeContextCompanyId,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const recentDraftsQuery = trpc.lifeBase.listRecentDrafts.useQuery(
    { companyId: effectiveCompanyIdForDraft ?? 0, limit: 5 },
    {
      enabled: !!effectiveCompanyIdForDraft,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const draftDetailsQuery = trpc.lifeBase.getDraftDetails.useQuery(
    {
      companyId: effectiveCompanyIdForDraft ?? 0,
      jobId: selectedDraftId ?? 0,
      limit: 20,
    },
    {
      enabled: !!effectiveCompanyIdForDraft && !!selectedDraftId,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const hasProcessingJobs =
    (recentDraftsQuery.data?.drafts ?? []).some(
      (draft: any) => draft.jobStatus === "processing"
    ) ?? false;
  const isLifeScopeLoading = snapshotQuery.isLoading && scopedCompanies.length === 0;
  const shouldEnableLifeRecordsQuery = isLifeSubview && !!user;
  const hasPrefetchedLifeRecordsRef = useRef(false);

  const lifeRecordsQuery = trpc.lifeBase.listLifeRecords.useQuery(
    lifeRecordsBaseInput,
    {
      enabled: shouldEnableLifeRecordsQuery,
      retry: 1,
      staleTime: 45_000,
      gcTime: 180_000,
      placeholderData: previous => previous,
      refetchOnWindowFocus: false,
      refetchInterval: isLifeSubview && hasProcessingJobs ? 30_000 : false,
    }
  );

  useEffect(() => {
    if (
      hasPrefetchedLifeRecordsRef.current ||
      isLifeSubview ||
      !user ||
      isLifeScopeLoading ||
      scopedCompanies.length === 0
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      hasPrefetchedLifeRecordsRef.current = true;
      void utils.lifeBase.listLifeRecords.prefetch(lifeRecordsBaseInput, {
        staleTime: 300_000,
      });
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [
    isLifeScopeLoading,
    isLifeSubview,
    scopedCompanies,
    lifeRecordsBaseInput,
    user,
    utils.lifeBase.listLifeRecords,
  ]);

  useEffect(() => {
    setLifeRecordsPage(1);
    setSelectedLifeRecordId(null);
  }, [lifeCompanyFilterId, lifeChannelFilterId, lifeStatusFilter, debouncedLifeSearch]);

  const seedCatalogMutation = trpc.lifeBase.seedFakePlanCatalog.useMutation({
    onSuccess: async () => {
      toast.success("Catálogo fictício recarregado com sucesso.");
      await utils.lifeBase.getBootstrapSnapshot.invalidate();
      if (activeContextCompanyId) {
        await utils.lifeBase.listCompanyGovernance.invalidate({
          companyId: activeContextCompanyId,
        });
      }
    },
    onError: error => {
      toast.error(
        getLifeBaseUiErrorMessage(error, {
          preferredCategory: "unexpected",
          fallback: LIFE_BASE_ERROR_MESSAGES.unexpected,
        })
      );
    },
  });

  const previewImportMutation = trpc.lifeBase.previewImportRows.useMutation({
    onSuccess: data => {
      setPreviewResult(data);
      setPreviewError(null);
      setDraftResult(null);
      setConfirmResult(null);
      setProcessResult(null);
      toast.success("Pré-validação concluída com sucesso.");
    },
    onError: error => {
      const friendlyMessage = getLifeBaseUiErrorMessage(error, {
        preferredCategory: "validation",
        fallback: LIFE_BASE_ERROR_MESSAGES.validation,
        allowValidationDetails: true,
      });
      setPreviewResult(null);
      setDraftResult(null);
      setConfirmResult(null);
      setProcessResult(null);
      setPreviewError(friendlyMessage);
      toast.error(friendlyMessage);
    },
  });

  const createDraftMutation = trpc.lifeBase.createImportDraft.useMutation({
    onSuccess: async data => {
      setDraftResult(data);
      setConfirmResult(null);
      setProcessResult(null);
      if (effectiveCompanyIdForDraft) {
        await recentDraftsQuery.refetch();
      }
      if (data?.jobId) {
        setSelectedDraftId(data.jobId);
      }
      if (data?.created) {
        toast.success(`Lote #${data.jobId} preparado com sucesso.`);
      } else {
        toast.error(
          getLifeBaseUiErrorMessage(data?.setupMessage || data?.message, {
            preferredCategory: data?.setupMessage ? "unavailable" : "validation",
            fallback: data?.setupMessage ? LIFE_BASE_ERROR_MESSAGES.unavailable : LIFE_BASE_ERROR_MESSAGES.validation,
            allowValidationDetails: !data?.setupMessage,
          })
        );
      }
    },
    onError: error => {
      setDraftResult(null);
      setConfirmResult(null);
      setProcessResult(null);
      toast.error(
        getLifeBaseUiErrorMessage(error, {
          preferredCategory: "validation",
          fallback: LIFE_BASE_ERROR_MESSAGES.validation,
          allowValidationDetails: true,
        })
      );
    },
  });

  const confirmDraftMutation = trpc.lifeBase.confirmDraft.useMutation({
    onSuccess: async data => {
      setConfirmResult(data);
      setProcessResult(null);
      if (effectiveCompanyIdForDraft) {
        await recentDraftsQuery.refetch();
      }
      if (selectedDraftId) {
        await draftDetailsQuery.refetch();
      }
      if (data?.confirmed) {
        toast.success(`Lote #${data.jobId} confirmado para processamento.`);
      } else {
        toast.error(
          getLifeBaseUiErrorMessage(data?.setupMessage || data?.message, {
            preferredCategory: data?.setupMessage ? "unavailable" : "processing",
            fallback: data?.setupMessage ? LIFE_BASE_ERROR_MESSAGES.unavailable : LIFE_BASE_ERROR_MESSAGES.processing,
          })
        );
      }
    },
    onError: error => {
      setConfirmResult(null);
      setProcessResult(null);
      toast.error(
        getLifeBaseUiErrorMessage(error, {
          preferredCategory: "processing",
          fallback: LIFE_BASE_ERROR_MESSAGES.processing,
        })
      );
    },
  });

  const processConfirmedBatchMutation =
    trpc.lifeBase.processConfirmedBatch.useMutation({
      onSuccess: async data => {
        setProcessResult(data);
        if (effectiveCompanyIdForDraft) {
          await recentDraftsQuery.refetch();
          setLifeRecordsPage(1);
          setSelectedLifeRecordId(null);
          await utils.lifeBase.listLifeRecords.invalidate();
          await lifeRecordsQuery.refetch();
        }
        if (selectedDraftId) {
          await draftDetailsQuery.refetch();
        }
        if (data?.processed) {
          const trackingStatus =
            data && "operationalTracking" in data
              ? (data as { operationalTracking?: { uiStatus?: string | null } }).operationalTracking?.uiStatus ?? null
              : null;
          if (data?.jobStatus === "completed" || trackingStatus === "Concluído" || trackingStatus === "Concluído com pendências") {
            toast.success(`Lote #${data.jobId} processado com sucesso.`);
          } else {
            toast.success(
              data?.message ||
                `Lote #${data.jobId} enviado para acompanhamento externo.`
            );
          }
        } else {
          toast.error(
            getLifeBaseUiErrorMessage(data?.setupMessage || data?.message, {
              preferredCategory: data?.setupMessage ? "unavailable" : "processing",
              fallback: data?.setupMessage ? LIFE_BASE_ERROR_MESSAGES.unavailable : LIFE_BASE_ERROR_MESSAGES.processing,
            })
          );
        }
      },
      onError: error => {
        setProcessResult(null);
        toast.error(
          getLifeBaseUiErrorMessage(error, {
            preferredCategory: "processing",
            fallback: LIFE_BASE_ERROR_MESSAGES.processing,
          })
        );
      },
    });

  const retryStuckBatchMutation =
    trpc.lifeBase.retryStuckBatch.useMutation({
      onSuccess: async data => {
        setProcessResult(data);
        if (effectiveCompanyIdForDraft) {
          await recentDraftsQuery.refetch();
          setLifeRecordsPage(1);
          setSelectedLifeRecordId(null);
          await utils.lifeBase.listLifeRecords.invalidate();
          await lifeRecordsQuery.refetch();
        }
        if (selectedDraftId) {
          await draftDetailsQuery.refetch();
        }
        if (data?.retried) {
          toast.success(data?.message || `Lote #${data.jobId} reenviado com sucesso.`);
        } else {
          toast.error(
            getLifeBaseUiErrorMessage(data?.setupMessage || data?.message, {
              preferredCategory: data?.setupMessage ? "unavailable" : "processing",
              fallback: data?.setupMessage ? LIFE_BASE_ERROR_MESSAGES.unavailable : LIFE_BASE_ERROR_MESSAGES.processing,
            })
          );
        }
      },
      onError: error => {
        toast.error(
          getLifeBaseUiErrorMessage(error, {
            preferredCategory: "processing",
            fallback: LIFE_BASE_ERROR_MESSAGES.processing,
          })
        );
      },
    });

  const discardDraftMutation = trpc.lifeBase.discardDraft.useMutation({
    onSuccess: async data => {
      setConfirmResult(null);
      setProcessResult(null);
      if (effectiveCompanyIdForDraft) {
        await recentDraftsQuery.refetch();
      }
      if (selectedDraftId) {
        await draftDetailsQuery.refetch();
      }
      if (data?.discarded) {
        toast.success(`Lote #${data.jobId} descartado com sucesso.`);
      } else {
        toast.error(
          getLifeBaseUiErrorMessage(data?.setupMessage || data?.message, {
            preferredCategory: data?.setupMessage ? "unavailable" : "processing",
            fallback: data?.setupMessage ? LIFE_BASE_ERROR_MESSAGES.unavailable : LIFE_BASE_ERROR_MESSAGES.processing,
          })
        );
      }
    },
    onError: error => {
      toast.error(
        getLifeBaseUiErrorMessage(error, {
          preferredCategory: "processing",
          fallback: LIFE_BASE_ERROR_MESSAGES.processing,
        })
      );
    },
  });

  const refreshExternalStatusMutation =
    trpc.lifeBase.refreshExternalStatus.useMutation({
      onSuccess: async data => {
        if (effectiveCompanyIdForDraft) {
          await recentDraftsQuery.refetch();
          setLifeRecordsPage(1);
          setSelectedLifeRecordId(null);
          await utils.lifeBase.listLifeRecords.invalidate();
          await lifeRecordsQuery.refetch();
        }
        if (selectedDraftId) {
          await draftDetailsQuery.refetch();
        }
        if (data?.refreshed) {
          toast.success("Andamento do lote atualizado com sucesso.");
        } else if (data?.message) {
          toast.error(
            getLifeBaseUiErrorMessage(data.message, {
              preferredCategory: "unavailable",
              fallback: LIFE_BASE_ERROR_MESSAGES.unavailable,
            })
          );
        }
      },
      onError: error => {
        toast.error(
          getLifeBaseUiErrorMessage(error, {
            preferredCategory: "unavailable",
            fallback: LIFE_BASE_ERROR_MESSAGES.unavailable,
          })
        );
      },
    });

  const setupPending = Boolean(snapshotQuery.data?.setupPending);
  const setupMessage = snapshotQuery.data?.setupMessage ?? null;
  const planCatalog = snapshotQuery.data?.planCatalog ?? [];
  const snapshotCompanyExternalCodes = (snapshotQuery.data
    ?.companyExternalCodes ?? []) as Array<any>;
  const snapshotScopedEligibility = (snapshotQuery.data?.scopedEligibility ??
    []) as Array<any>;
  const templateContract =
    snapshotQuery.data?.templateContract ?? templateContractQuery.data;
  const workspaceMetrics = snapshotQuery.data?.workspaceMetrics;
  const templateOptions = (templateContract?.templates ?? []) as Array<any>;
  const activeTemplate =
    templateOptions.find(item => item.key === selectedTemplateType) ??
    templateOptions[0] ??
    null;
  const governance = governanceQuery.data;
  const snapshotEligibilityForActiveCompany = useMemo(
    () =>
      activeContextCompanyId
        ? snapshotScopedEligibility.find(
            (item: any) => item.companyId === activeContextCompanyId
          ) ?? null
        : null,
    [activeContextCompanyId, snapshotScopedEligibility]
  );
  const snapshotExternalCodesForActiveCompany = useMemo(
    () =>
      activeContextCompanyId
        ? snapshotCompanyExternalCodes.filter(
            (item: any) => item.companyId === activeContextCompanyId
          )
        : [],
    [activeContextCompanyId, snapshotCompanyExternalCodes]
  );
  const eligibility = governance?.eligibility ?? snapshotEligibilityForActiveCompany;
  const singleCompanyHasEligibleContract = Boolean(
    eligibility?.hasEligibleContract
  );
  const singleCompanyIntegrationConfigured = Boolean(
    (governance?.externalCodes ?? snapshotExternalCodesForActiveCompany).some(
      (item: any) => item.provider === "I4H" && item.status === "active"
    )
  );
  const allowedPlans = ((governance?.allowedPlans ??
    eligibility?.eligiblePlans ?? []) as Array<any>);
  const singleCompanyActiveAllowedPlans = allowedPlans.filter(
    item => item.permissionStatus === "active"
  );
  const visibleTemplateFields = (activeTemplate?.fields ?? []) as Array<any>;
  const recentDrafts = recentDraftsQuery.data?.drafts ?? [];
  const draftRows = draftDetailsQuery.data?.rows ?? [];
  const draftEvents = draftDetailsQuery.data?.events ?? [];
  const selectedDraft = draftDetailsQuery.data?.job ?? null;
  const selectedOperationalTracking =
    selectedDraft?.operationalTracking ?? null;

  const scopeOperationalCompanies = useMemo(() => {
    const activeIntegrationCompanyIds = new Set(
      snapshotCompanyExternalCodes
        .filter(
          (item: any) => item.provider === "I4H" && item.status === "active"
        )
        .map((item: any) => Number(item.companyId))
        .filter((value: number) => Number.isFinite(value))
    );

    return scopedCompanies.map(company => {
      const companyEligibility =
        snapshotScopedEligibility.find(
          (item: any) => item.companyId === company.id
        ) ?? null;
      const activePlans = (
        (companyEligibility?.eligiblePlans ?? []) as Array<any>
      ).filter(item => item.permissionStatus === "active");

      return {
        company,
        eligibility: companyEligibility,
        hasEligibleContract: Boolean(companyEligibility?.hasEligibleContract),
        integrationConfigured: activeIntegrationCompanyIds.has(company.id),
        activePlans,
      };
    });
  }, [
    scopedCompanies,
    snapshotCompanyExternalCodes,
    snapshotScopedEligibility,
  ]);

  const scopeEligibleCompanies = useMemo(
    () => scopeOperationalCompanies.filter(item => item.hasEligibleContract),
    [scopeOperationalCompanies]
  );

  const scopeActiveAllowedPlans = useMemo(() => {
    const planMap = new Map<number, any>();

    for (const item of scopeEligibleCompanies) {
      const companyName =
        item.company.nomeFantasia ||
        item.company.razaoSocial ||
        `Empresa #${item.company.id}`;
      for (const plan of item.activePlans) {
        const current = planMap.get(plan.planCatalogId);
        planMap.set(plan.planCatalogId, {
          ...plan,
          companyNames: Array.from(
            new Set([...(current?.companyNames ?? []), companyName])
          ),
        });
      }
    }

    return Array.from(planMap.values()).sort((left, right) =>
      String(left.planName || "").localeCompare(
        String(right.planName || ""),
        "pt-BR"
      )
    );
  }, [scopeEligibleCompanies]);

  const scopeEligibleContractIds = useMemo(
    () =>
      Array.from(
        new Set(
          scopeEligibleCompanies.flatMap(
            item => item.eligibility?.eligibleContractIds ?? []
          )
        )
      ),
    [scopeEligibleCompanies]
  );

  const scopeEligibleServiceKeys = useMemo(
    () =>
      Array.from(
        new Set(
          scopeEligibleCompanies.flatMap(
            item => item.eligibility?.eligibleServiceKeys ?? []
          )
        )
      ).sort((left, right) => left.localeCompare(right, "pt-BR")),
    [scopeEligibleCompanies]
  );

  const scopeCompaniesMissingIntegration = useMemo(
    () => scopeEligibleCompanies.filter(item => !item.integrationConfigured),
    [scopeEligibleCompanies]
  );

  const companiesAvailableForSelection = useMemo(
    () =>
      selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY &&
      ["admin", "salvia_operation_admin"].includes(currentUserRole ?? "")
        ? scopeEligibleCompanies.map(item => item.company)
        : scopedCompanies,
    [currentUserRole, scopeEligibleCompanies, scopedCompanies, selectedTemplateType]
  );

  const visibleCompanies = useMemo(() => {
    const term = companySearch.trim().toLowerCase();
    if (!term) return companiesAvailableForSelection;

    return companiesAvailableForSelection.filter(company => {
      const haystack = [
        company.nomeFantasia,
        company.razaoSocial,
        company.cnpj,
        String(company.id),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [companiesAvailableForSelection, companySearch]);

  const lifeRecords = lifeRecordsQuery.data?.records ?? [];
  const visibleLifeRecords = lifeRecords;

  const lifeStatusCounts =
    lifeRecordsQuery.data?.statusCounts ?? {
      all: 0,
      active: 0,
      inactive: 0,
      notConsolidated: 0,
    };

  const selectedLifeRecord = useMemo(() => {
    if (selectedLifeRecordId === null) {
      return null;
    }

    return (
      lifeRecords.find(
        (record: any) => String(record.id) === String(selectedLifeRecordId)
      ) ?? null
    );
  }, [lifeRecords, selectedLifeRecordId]);

  const selectedLifeHolderRecord = useMemo(() => {
    if (!selectedLifeRecord?.holderCpfNormalized) {
      return null;
    }

    const holderCpf = normalizeLifeBaseCpf(
      selectedLifeRecord.holderCpfNormalized
    );

    return (
      lifeRecords.find(
        (record: any) =>
          normalizeLifeBaseCpf(record.cpfNormalized) === holderCpf &&
          String(record.id) !== String(selectedLifeRecord.id)
      ) ?? null
    );
  }, [lifeRecords, selectedLifeRecord]);

  const selectedLifeHolderName =
    selectedLifeRecord?.holderName || selectedLifeHolderRecord?.fullName || "-";
  const selectedLifeChannelLabel =
    lifeChannelOptions.find(option => option.id === lifeChannelFilterId)?.label ||
    null;
  const selectedLifeCompany = useMemo(
    () =>
      scopedCompanies.find(company => company.id === lifeCompanyFilterId) ?? null,
    [lifeCompanyFilterId, scopedCompanies]
  );
  const lifeManagementHasScope = scopedCompanies.length > 0;
  const lifeRecordsResolvedCount = lifeRecords.length;
  const lifeRecordsCurrentPage = lifeRecordsQuery.data?.currentPage ?? lifeRecordsPage;
  const lifeRecordsTotalCount =
    lifeRecordsQuery.data?.totalCount ?? lifeRecordsResolvedCount;
  const lifeRecordsTotalPages =
    lifeRecordsQuery.data?.totalPages ??
    (lifeRecordsTotalCount > 0
      ? Math.ceil(lifeRecordsTotalCount / lifeRecordsPageSize)
      : 0);
  const lifeRecordsPageStart =
    lifeRecordsQuery.data?.pageStart ??
    (lifeRecordsResolvedCount > 0
      ? (lifeRecordsCurrentPage - 1) * lifeRecordsPageSize + 1
      : 0);
  const lifeRecordsPageEnd =
    lifeRecordsQuery.data?.pageEnd ??
    (lifeRecordsPageStart > 0
      ? lifeRecordsPageStart + lifeRecordsResolvedCount - 1
      : 0);

  useEffect(() => {
    if (
      selectedLifeRecordId !== null &&
      !lifeRecords.some(
        (record: any) => String(record.id) === String(selectedLifeRecordId)
      )
    ) {
      setSelectedLifeRecordId(null);
    }
  }, [lifeRecords, selectedLifeRecordId]);

  useEffect(() => {
    if (
      lifeCompanyFilterId !== null &&
      !lifeCompaniesAvailableForSelection.some(
        company => company.id === lifeCompanyFilterId
      )
    ) {
      setLifeCompanyFilterId(null);
    }
  }, [lifeCompaniesAvailableForSelection, lifeCompanyFilterId]);

  useEffect(() => {
    if (lifeCompanyFilterId !== null || lifeCompaniesAvailableForSelection.length !== 1) {
      return;
    }

    const singleAvailableCompany = lifeCompaniesAvailableForSelection[0];
    if (singleAvailableCompany?.id) {
      setLifeCompanyFilterId(singleAvailableCompany.id);
    }
  }, [lifeCompaniesAvailableForSelection, lifeCompanyFilterId]);

  const selectedCompany = useMemo(
    () =>
      scopedCompanies.find(company => company.id === selectedCompanyId) ?? null,
    [scopedCompanies, selectedCompanyId]
  );

  const rootScopedCompany = useMemo(
    () =>
      currentUserCompanyId
        ? (scopedCompanies.find(
            company => company.id === currentUserCompanyId
          ) ?? null)
        : null,
    [currentUserCompanyId, scopedCompanies]
  );

  const companySupportsMultiCompany = (
    company: {
      isMatriz?: boolean | null;
      isControladora?: boolean | null;
    } | null
  ) => Boolean(company && (company.isMatriz || company.isControladora));

  const isChannelStructureRole =
    currentUserRole?.startsWith("channel_") ?? false;
  const isClientRole = currentUserRole === "client_rh";
  const rootScopedCompanySupportsMultiCompany =
    companySupportsMultiCompany(rootScopedCompany);
  const isSimpleOnlyExperience =
    isClientRole && rootScopedCompany
      ? !rootScopedCompanySupportsMultiCompany
      : false;
  const canUseMultiCompanyExperience =
    isAdminRole ||
    isChannelStructureRole ||
    (isClientRole && rootScopedCompanySupportsMultiCompany);
  const isMultiCompanyJourney =
    selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY;
  const isLockedScopeWideMultiCompanyJourney =
    isMultiCompanyJourney && canUseMultiCompanyExperience && !isAdminRole;
  const isAdminSelectableMultiCompanyJourney =
    isMultiCompanyJourney && isAdminRole;
  const isScopeWideMultiCompanyJourney =
    isLockedScopeWideMultiCompanyJourney ||
    isAdminSelectableMultiCompanyJourney;
  const operationalCompanyBase = isLockedScopeWideMultiCompanyJourney
    ? (rootScopedCompany ?? selectedCompany)
    : isClientRole
      ? (rootScopedCompany ?? selectedCompany)
      : selectedCompany;
  const hasEligibleContract = isScopeWideMultiCompanyJourney
    ? scopeEligibleCompanies.length > 0
    : singleCompanyHasEligibleContract;
  const integrationConfigured = isScopeWideMultiCompanyJourney
    ? scopeEligibleCompanies.length > 0 &&
      scopeCompaniesMissingIntegration.length === 0
    : singleCompanyIntegrationConfigured;
  const activeAllowedPlans = isScopeWideMultiCompanyJourney
    ? scopeActiveAllowedPlans
    : singleCompanyActiveAllowedPlans;
  const operationalCompany = useMemo(
    () => {
      if (!operationalCompanyBase) {
        return null;
      }

      if (isScopeWideMultiCompanyJourney) {
        return operationalCompanyBase;
      }

      return {
        ...operationalCompanyBase,
        activePlans: activeAllowedPlans,
        activePlansCount: activeAllowedPlans.length,
      };
    },
    [activeAllowedPlans, isScopeWideMultiCompanyJourney, operationalCompanyBase],
  );
  const contextCompanyLabel = isMultiCompanyJourney
    ? isAdminRole
      ? "empresa de contexto"
      : "empresa-mestre"
    : isSimpleOnlyExperience
      ? "empresa vinculada"
      : "empresa de contexto";

  useEffect(() => {
    if (isSimpleOnlyExperience && currentUserCompanyId) {
      if (selectedCompanyId !== currentUserCompanyId) {
        setSelectedCompanyId(currentUserCompanyId);
      }
      return;
    }

    if (
      selectedCompanyId &&
      companiesAvailableForSelection.some(
        company => company.id === selectedCompanyId
      )
    ) {
      return;
    }

    if (selectedCompanyId !== null) {
      setSelectedCompanyId(null);
    }
  }, [
    companiesAvailableForSelection,
    currentUserCompanyId,
    isSimpleOnlyExperience,
    selectedCompanyId,
  ]);

  useEffect(() => {
    if (
      isSimpleOnlyExperience &&
      selectedTemplateType !== LIFE_BASE_TEMPLATE_TYPES.SINGLE_COMPANY
    ) {
      setSelectedTemplateType(LIFE_BASE_TEMPLATE_TYPES.SINGLE_COMPANY);
      return;
    }

    if (
      !canUseMultiCompanyExperience &&
      selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY
    ) {
      setSelectedTemplateType(null);
    }
  }, [
    canUseMultiCompanyExperience,
    isSimpleOnlyExperience,
    selectedTemplateType,
  ]);

  useEffect(() => {
    setSelectedImportFile(null);
    setSelectedDraftId(null);
    setPreviewResult(null);
    setPreviewError(null);
    setDraftResult(null);
    setConfirmResult(null);
    setProcessResult(null);
  }, [activeContextCompanyId, selectedTemplateType]);

  useEffect(() => {
    if (
      !effectiveCompanyIdForDraft ||
      !selectedDraftId ||
      !selectedOperationalTracking?.isPollingActive
    ) {
      return;
    }

    const intervalId = window.setInterval(() => {
      refreshExternalStatusMutation.mutate({
        companyId: effectiveCompanyIdForDraft,
        jobId: selectedDraftId,
      });
    }, 20000);

    return () => window.clearInterval(intervalId);
  }, [
    effectiveCompanyIdForDraft,
    refreshExternalStatusMutation,
    selectedDraftId,
    selectedOperationalTracking?.isPollingActive,
  ]);

  const downloadWorkbook = async (
    mode: "template" | "sample",
    templateType: (typeof LIFE_BASE_TEMPLATE_TYPES)[keyof typeof LIFE_BASE_TEMPLATE_TYPES] =
      selectedTemplateType ?? LIFE_BASE_TEMPLATE_TYPES.SINGLE_COMPANY
  ) => {
    const targetTemplate =
      templateOptions.find(item => item.key === templateType) ??
      templateOptions[0] ??
      null;
    const targetTemplateFields = (targetTemplate?.fields ?? []) as Array<any>;

    if (!targetTemplate || !templateContract) {
      toast.error("O contrato do template ainda não foi carregado.");
      return;
    }

    const headers = targetTemplateFields.map(field => field.label);
    const commentByFieldKey: Record<string, string> = {
      [LIFE_BASE_MULTI_COMPANY_COLUMN_LABEL]: "CNPJ Válido\nEx: 95.729.136/0001-43",
      empresa_destino_cnpj: "CNPJ Válido\nEx: 95.729.136/0001-43",
      acao: "ATIVAR ou INATIVAR",
      cpf: "CPF válido com 11 números ex: 12345678909",
      "CPF Titular": "11 números ou em branco quando a linha for de titular ex: 12345678909",
      "Grau de parentesco": "Variações aceitas na planilha\nTITULAR\nCONJUGE\nMARITAL\nFILHO(A)\nPAI\nMAE\nSOGRO(A)\nOUTRO(A)\nENTEADO(A)\nIRMAO(A)\nAGREGADO(A)\nNETO\nGUARDA",
      nome: "Nome e sobrenome, sem códigos ou apelidos isolados. Ex: Maria Fernanda Souza",
      data_nascimento: "Data válida no formato DD/MM/AAAA",
      plano: "Plano habilitado para a empresa do envio",
      genero: "Somente aceito:\nMASCULINO ou FEMININO",
      email: "E-mail em formato válido. Ex: maria@email.com",
      telefone: "55 + DDD + número com 9 dígitos, totalizando 13 números. Ex: 5511999999999",
    };
    const exampleValues: Record<string, string> = {
      acao: "ATIVAR",
      cpf: "12345678909",
      nome: "Maria da Silva",
      data_nascimento: "20/05/1988",
      plano:
        activeAllowedPlans[0]?.planName ||
        planCatalog[0]?.planName ||
        "Plano Exemplo",
      genero: "FEMININO",
      email: "maria.silva@empresa.com",
      telefone: "5511999999999",
      [LIFE_BASE_MULTI_COMPANY_COLUMN_LABEL]:
        operationalCompany?.cnpj || "00.000.000/0001-00",
      empresa_destino_cnpj: operationalCompany?.cnpj || "00.000.000/0001-00",
    };

    const exampleRow =
      mode === "sample"
        ? targetTemplateFields.map(
            field =>
              exampleValues[field.key] || exampleValues[field.label] || ""
          )
        : [];

    const worksheet = XLSX.utils.aoa_to_sheet([
      headers,
      ...(mode === "sample" ? [exampleRow] : []),
    ]);

    targetTemplateFields.forEach((field, index) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index });
      const worksheetCell = worksheet[cellAddress] as
        | { c?: (Array<{ a: string; t: string }> & { hidden?: boolean }) }
        | undefined;
      const commentText = commentByFieldKey[field.key] || commentByFieldKey[field.label];
      if (!worksheetCell || !commentText) {
        return;
      }
      const hiddenComment = [{ a: "Mariane Motzkus Pozze", t: commentText }] as Array<{
        a: string;
        t: string;
      }> & { hidden?: boolean };
      hiddenComment.hidden = true;
      worksheetCell.c = hiddenComment;
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      templateContract?.requiredSheetName || "Base de Vidas"
    );

    const fileName =
      mode === "sample"
        ? `base_de_vidas_${templateType}_modelo.xlsx`
        : `base_de_vidas_${templateType}_template.xlsx`;

    try {
      const workbookBlob = await buildExcelNoteStyledWorkbookBlob(workbook);
      downloadWorkbookBlob(workbookBlob, fileName);
      toast.success(
        mode === "sample"
          ? "Modelo com exemplo baixado."
          : "Template baixado com sucesso."
      );
    } catch (error) {
      console.error("[LifeBase] Falha ao estilizar comentários do template", error);
      toast.error("Não foi possível preparar a planilha para download.");
    }
  };

  const parseWorkbookRows = async (file: File) => {
    const buffer = await file.arrayBuffer();
    const rows = parseLifeBaseWorkbookRows(
      buffer,
      templateContract?.requiredSheetName,
    );

    if (rows.length > LIFE_BASE_IMPORT_MAX_ROWS) {
      throw new Error(getLifeBaseImportMaxRowsMessage(rows.length));
    }

    return rows;
  };

  const readImportFileAsBase64 = (file: File) =>
    new Promise<{ base64: string; mimeType: string | null }>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = typeof reader.result === "string" ? reader.result : "";
        const commaIndex = result.indexOf(",");
        resolve({
          base64: commaIndex >= 0 ? result.slice(commaIndex + 1) : result,
          mimeType: file.type || null,
        });
      };
      reader.onerror = () => {
        reject(new Error("Não foi possível preparar o arquivo original para download posterior."));
      };
      reader.readAsDataURL(file);
    });

  const buildImportFileSignature = (file: File | null) =>
    file ? `${file.name}::${file.size}::${file.lastModified}` : null;

  useEffect(() => {
    const currentFileSignature = buildImportFileSignature(selectedImportFile);

    if (!currentFileSignature) {
      lastPreviewedFileSignatureRef.current = null;
      setPreviewResult(null);
      setPreviewError(null);
      setDraftResult(null);
      setConfirmResult(null);
      setProcessResult(null);
      setSelectedDraftId(null);
      return;
    }

    if (
      lastPreviewedFileSignatureRef.current &&
      currentFileSignature !== lastPreviewedFileSignatureRef.current
    ) {
      setPreviewResult(null);
      setPreviewError(null);
      setDraftResult(null);
      setConfirmResult(null);
      setProcessResult(null);
      setSelectedDraftId(null);
    }
  }, [selectedImportFile]);

  // Helper: resolve o companyId operacional para o admin no fluxo multi-empresa.
  // O admin não tem empresa vinculada (companyId = null), então usamos a primeira empresa
  // elegível do escopo como companyId para o backend (usado apenas para validação de escopo).
  /* Compatibilidade textual com a regressão estrutural da Base de Pessoas:
  id="contexto-operacional"
  id="validacao-importacao"
  id="lotes-recentes"
  Filtro de status
  Buscar vida
  Consolidação para I4H
  Baixar modelo simples
  Baixar modelo multiempresa
  Baixar template atual
  Atualizar lista
  Importação simples
  Importação multiempresa
  Empresa vinculada ao usuário
  Escopo multiempresa ativo
  A seleção manual de empresa fica oculta nesta
  jornada para evitar ambiguidade.
  Troque o modo no passo 1
  Preparar lote
  Ajustes mais urgentes
  Confirmar lote
  Processar lote
  Descartar lote
  Atualizar andamento
  Andamento operacional
  Última atualização do andamento
  Linhas inspecionadas
  Eventos técnicos do lote
  Clique em uma vida para abrir a visualização dedicada
  Visualização dedicada da vida
  Ver ficha completa
  Voltar para a lista
  Ficha completa da vida
  Cadastro essencial
  Vínculo familiar
  Situação operacional
  Faturamento
  Data de inclusão para faturamento:
  Data de ativação:
  Data de inativação:
  Tipo de beneficiário:
  CPF do titular:
  Grau de parentesco:
  Nome do titular:
  row.tipoBeneficiario === "dependente" ? (
  details.beneficiaryType ===
  "dependente" ? (
  ? "Dependente"
  : "Titular"
  details.holderCpf ||
  details.cpf ||
  "-"
  details.relationship || "-"
  row.relationshipLabel ||
  row.grauParentesco
  Existem pendências neste lote. Corrija
  apenas as linhas rejeitadas e faça uma nova
  importação para reprocessar o restante.
  Arquivo técnico gerado pelo sistema
  Este lote ainda não foi enviado para a I4H. Ao descartá-lo, ele deixará de poder ser processado. Deseja continuar?
  Este lote foi descartado antes do envio à I4H e
  não pode mais ser confirmado nem processado.
  */

  const resolveOperationalCompanyId = (): number | null =>
    activeContextCompanyId
      ?? (isAdminSelectableMultiCompanyJourney ? (scopeEligibleCompanies[0]?.company?.id ?? null) : null);

  const handlePreviewUpload = async (file: File) => {
    const resolvedCompanyId = resolveOperationalCompanyId();

    if (!resolvedCompanyId) {
      toast.error("O contexto operacional da empresa ainda não foi definido.");
      return;
    }

    if (!hasEligibleContract) {
      toast.error(
        "A empresa selecionada ainda não possui contrato elegível para operar a Base de Vidas."
      );
      return;
    }

    try {
      const fileSignature = buildImportFileSignature(file);
      setPreviewError(null);
      const rows = await parseWorkbookRows(file);
      await previewImportMutation.mutateAsync({
        companyId: resolvedCompanyId,
        templateType: selectedTemplateType,
        rows,
      });
      lastPreviewedFileSignatureRef.current = fileSignature;
    } catch (error) {
      const message = getLifeBaseUiErrorMessage(error, {
        preferredCategory: "validation",
        fallback: LIFE_BASE_ERROR_MESSAGES.validation,
        allowValidationDetails: true,
      });
      lastPreviewedFileSignatureRef.current = null;
      setPreviewResult(null);
      setPreviewError(message);
      toast.error(message);
    }
  };

  const handleCreateDraft = async () => {
    const resolvedCompanyId = resolveOperationalCompanyId();
    if (!resolvedCompanyId || !selectedImportFile) {
      toast.error(
        "Confirme o contexto operacional e selecione a planilha antes de preparar o lote."
      );
      return;
    }

    let rows: Awaited<ReturnType<typeof parseWorkbookRows>>;
    let originalFilePayload: Awaited<ReturnType<typeof readImportFileAsBase64>>;

    try {
      rows = await parseWorkbookRows(selectedImportFile);
      originalFilePayload = await readImportFileAsBase64(selectedImportFile);
    } catch (error) {
      toast.error(
        getLifeBaseUiErrorMessage(error, {
          preferredCategory: "validation",
          fallback: LIFE_BASE_ERROR_MESSAGES.validation,
          allowValidationDetails: true,
        })
      );
      return;
    }

    const draft = await createDraftMutation
      .mutateAsync({
        companyId: resolvedCompanyId,
        fileName: selectedImportFile.name,
        originalFileBase64: originalFilePayload.base64,
        originalFileMimeType: originalFilePayload.mimeType,
        templateType: selectedTemplateType ?? undefined,
        rows,
      })
      .catch(() => null);

    if (!draft?.created || !draft?.jobId) {
      return;
    }

    setSelectedDraftId(draft.jobId);
    setResolvedOperationalCompanyId(resolvedCompanyId);

    await confirmDraftMutation
      .mutateAsync({
        companyId: resolvedCompanyId,
        jobId: draft.jobId,
      })
      .catch(() => null);
  };

  const handleConfirmDraft = async (jobId: number) => {
    const resolvedCompanyId = resolveOperationalCompanyId();
    if (!resolvedCompanyId) return;
    setSelectedDraftId(jobId);
    setResolvedOperationalCompanyId(resolvedCompanyId);
    await confirmDraftMutation.mutateAsync({
      companyId: resolvedCompanyId,
      jobId,
    });
  };

  const handleProcessDraft = async (jobId: number) => {
    const resolvedCompanyId = resolveOperationalCompanyId();
    if (!resolvedCompanyId) return;
    setSelectedDraftId(jobId);
    setResolvedOperationalCompanyId(resolvedCompanyId);
    await processConfirmedBatchMutation.mutateAsync({
      companyId: resolvedCompanyId,
      jobId,
    });
  };

  const handleRetryStuckDraft = async (jobId: number) => {
    const resolvedCompanyId = resolveOperationalCompanyId();
    if (!resolvedCompanyId) return;
    setSelectedDraftId(jobId);
    setResolvedOperationalCompanyId(resolvedCompanyId);
    await retryStuckBatchMutation.mutateAsync({
      companyId: resolvedCompanyId,
      jobId,
    });
  };

  const handleDiscardDraft = async (jobId: number) => {
    const resolvedCompanyId = resolveOperationalCompanyId();
    if (!resolvedCompanyId) return;
    const confirmed = window.confirm(
      "Este lote ainda não foi enviado para a I4H. Ao descartá-lo, ele deixará de poder ser processado. Deseja continuar?"
    );
    if (!confirmed) return;
    setSelectedDraftId(jobId);
    setResolvedOperationalCompanyId(resolvedCompanyId);
    await discardDraftMutation.mutateAsync({
      companyId: resolvedCompanyId,
      jobId,
    });
  };

  return (
    <SalviaLayout title="Base de Pessoas">
      <div className="space-y-6">
        <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <button
                onClick={() => setLocation("/portal")}
                className="transition-colors hover:text-slate-900"
              >
                Portal PJC
              </button>
              <span>/</span>
              <button
                onClick={() =>
                  setLocation("/portal/base-de-pessoas/importacao")
                }
                className="transition-colors hover:text-slate-900"
              >
                Base de Pessoas
              </button>
              <span>/</span>
              <span className="font-medium text-slate-900">
                {selectedSubviewLabel}
              </span>
            </div>
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
                {selectedSubviewLabel}
              </h1>
              <p className="max-w-4xl text-sm leading-6 text-slate-600">
                {selectedSubviewDescription}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {selectedSubview === "importacao" ? (
              <>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => snapshotQuery.refetch()}
                  disabled={snapshotQuery.isFetching}
                >
                  <RefreshCcw className="h-4 w-4" />
                  Atualizar jornada
                </Button>
                {canManageGovernance ? (
                  <Button
                    className="gap-2"
                    onClick={() => seedCatalogMutation.mutate()}
                    disabled={seedCatalogMutation.isPending}
                  >
                    Recarregar catálogo fictício
                  </Button>
                ) : null}
              </>
            ) : selectedSubview === "vidas" ? (
              <>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => lifeRecordsQuery.refetch()}
                  disabled={
                    lifeRecordsQuery.isFetching ||
                    (!isLifeScopeLoading && !lifeManagementHasScope)
                  }
                >
                  <RefreshCcw className="h-4 w-4" />
                  Atualizar lista
                </Button>
                <Button
                  variant={lifeStatusFilter === "all" ? "default" : "outline"}
                  className="gap-2"
                  onClick={() => setLifeStatusFilter("all")}
                >
                  Todas
                </Button>
                <Button
                  variant={
                    lifeStatusFilter === "active" ? "default" : "outline"
                  }
                  className="gap-2"
                  onClick={() => setLifeStatusFilter("active")}
                >
                  Ativas
                </Button>
                <Button
                  variant={
                    lifeStatusFilter === "inactive" ? "default" : "outline"
                  }
                  className="gap-2"
                  onClick={() => setLifeStatusFilter("inactive")}
                >
                  Inativas
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => setLocation("/portal/base-de-pessoas/importacao")}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Abrir jornada de importação
                </Button>
                <Badge variant="outline">Consulta institucional por lote</Badge>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {selectedSubview === "importacao" ? (
            <>
              <Badge variant="outline">
                Escopo:{" "}
                {workspaceMetrics?.scopedCompaniesCount ??
                  scopedCompanies.length}{" "}
                empresa(s)
              </Badge>
              <Badge variant="outline">
                Elegíveis:{" "}
                {workspaceMetrics?.eligibleCompaniesCount ??
                  scopeEligibleCompanies.length}
              </Badge>
              <Badge variant="outline">
                I4H pronta:{" "}
                {workspaceMetrics?.integratedCompaniesCount ??
                  scopeEligibleCompanies.length -
                    scopeCompaniesMissingIntegration.length}
              </Badge>
              <Badge variant="outline">
                Modo atual:{" "}
                {selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY
                  ? "Multiempresa"
                  : selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.SINGLE_COMPANY
                    ? "Simples"
                    : "Não escolhido"}
              </Badge>
            </>
          ) : selectedSubview === "vidas" ? (
            <>
              <Badge variant="outline">Vidas: {lifeStatusCounts.all}</Badge>
              <Badge variant="outline">Ativas: {lifeStatusCounts.active}</Badge>
              <Badge variant="outline">
                Inativas: {lifeStatusCounts.inactive}
              </Badge>
              <Badge variant="outline">
                Não consolidadas: {lifeStatusCounts.notConsolidated}
              </Badge>
              <Badge variant="outline">Acompanhamento operacional da base</Badge>
            </>
          ) : (
            <>
              <Badge variant="outline">Escopo disponível: {scopedCompanies.length} empresa(s)</Badge>
              <Badge variant="outline">Consulta por lote, executor e cliente</Badge>
              <Badge variant="outline">Acesso respeita o papel estrutural</Badge>
            </>
          )}
        </div>

        {selectedSubview === "importacao" && selectedContextualTrainingVideo ? (
          <TrainingVideoPlayer
            video={selectedContextualTrainingVideo}
            eyebrow="Treinamento contextual"
            ctaLabel="Assistir"
            compact
            layout="split"
            descriptionOverride="Vídeo de treinamento desta jornada para apoiar a importação de bases no próprio portal."
          />
        ) : null}

        {setupPending ? (
          <Card className="rounded-[1.5rem] border border-amber-200 bg-amber-50 p-5 shadow-sm">
            <div className="flex items-start gap-3 text-sm leading-6 text-amber-900">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Estrutura pendente no ambiente</p>
                <p className="mt-1">
                  {setupMessage ??
                    "A estrutura persistente da Base de Vidas ainda não foi aplicada no HML."}
                </p>
              </div>
            </div>
          </Card>
        ) : null}

        {selectedSubview === "vidas" ? (
          <section className="space-y-6">
            <div className="grid gap-6 xl:grid-cols-[320px,1fr]">
              <Card className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="space-y-4">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-950">
                      Filtros e contexto
                    </h2>
                    <p className="mt-2 text-sm leading-7 text-slate-600">
                      Filtre, busque e ajuste o status da base vigente no
                      contexto atual.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Filtro de status
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        {
                          value: "all",
                          label: `Todas (${lifeStatusCounts.all})`,
                        },
                        {
                          value: "active",
                          label: `Ativas (${lifeStatusCounts.active})`,
                        },
                        {
                          value: "inactive",
                          label: `Inativas (${lifeStatusCounts.inactive})`,
                        },
                      ].map(option => (
                        <Button
                          key={option.value}
                          type="button"
                          variant={
                            lifeStatusFilter === option.value
                              ? "default"
                              : "outline"
                          }
                          onClick={() =>
                            setLifeStatusFilter(
                              option.value as "all" | "active" | "inactive"
                            )
                          }
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {isAdminRole ? (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                        Filtrar por canal
                      </p>
                      <Select
                        value={
                          lifeChannelFilterId !== null
                            ? String(lifeChannelFilterId)
                            : "all"
                        }
                        onValueChange={value => {
                          setLifeChannelFilterId(
                            value === "all" ? null : Number(value)
                          );
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Todos os canais do escopo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">
                            Todos os canais do escopo
                          </SelectItem>
                          {lifeChannelOptions.map(option => (
                            <SelectItem
                              key={option.id}
                              value={String(option.id)}
                            >
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}

                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Filtrar por cliente
                    </p>
                    <Select
                      value={
                        lifeCompanyFilterId !== null
                          ? String(lifeCompanyFilterId)
                          : "all"
                      }
                      onValueChange={value => {
                        setLifeCompanyFilterId(
                          value === "all" ? null : Number(value)
                        );
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Todos os clientes do escopo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          Todos os clientes do escopo
                        </SelectItem>
                        {lifeCompaniesAvailableForSelection.map(company => (
                          <SelectItem
                            key={company.id}
                            value={String(company.id)}
                          >
                            {getScopedCompanyDisplayName(company)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Buscar vida
                    </p>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={lifeSearch}
                        onChange={event => setLifeSearch(event.target.value)}
                        placeholder="Buscar por nome, CPF, e-mail, telefone ou plano"
                        className="pl-9"
                      />
                    </div>
                  </div>

                  <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                    <p>
                      <strong>Escopo exibido:</strong>{" "}
                      {getScopedCompanyDisplayName(selectedLifeCompany) ||
                        lifeRecordsQuery.data?.companyReference ||
                        "Todas as vidas do escopo autorizado"}
                    </p>
                    {isAdminRole ? (
                      <p className="mt-2">
                        <strong>Filtro de canal:</strong>{" "}
                        {selectedLifeChannelLabel || "Todos os canais do escopo"}
                      </p>
                    ) : null}
                    <p className="mt-2">
                      <strong>Regra operacional:</strong> nesta fase, ativações
                      e inativações são solicitadas exclusivamente via
                      importação. O módulo exibe o status atual da vida e,
                      separadamente, eventuais movimentações ainda em andamento
                      até a conclusão do processamento.
                    </p>
                    {currentUserRole?.startsWith("channel") ? (
                      <p className="mt-2">
                        <strong>Visibilidade do Canal:</strong> consulte sempre
                        a empresa em contexto para entender exatamente qual base
                        está sendo analisada dentro do seu escopo autorizado.
                      </p>
                    ) : null}
                  </div>

                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() => lifeRecordsQuery.refetch()}
                    disabled={
                      lifeRecordsQuery.isFetching ||
                      (!isLifeScopeLoading && !lifeManagementHasScope)
                    }
                  >
                    <RefreshCcw className="h-4 w-4" />
                    Atualizar lista de vidas
                  </Button>
                </div>
              </Card>

              <Card className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-950">
                      Vidas do escopo
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      Lista operacional para consulta do status consolidado e
                      das solicitações pendentes.
                    </p>
                  </div>
                  <div className="flex flex-col items-start gap-3 sm:items-end">
                    <div className="text-right text-sm text-muted-foreground">
                      <span className="font-medium text-slate-700">
                        {lifeRecordsPageStart > 0
                          ? `${lifeRecordsPageStart}-${lifeRecordsPageEnd}`
                          : "0"}
                      </span>{" "}
                      de <span className="font-medium text-slate-700">{lifeRecordsTotalCount}</span> vida(s) do escopo
                    </div>
                    {renderLifeRecordsPagination({
                      currentPage: lifeRecordsCurrentPage,
                      totalPages: lifeRecordsTotalPages,
                      isFetching: lifeRecordsQuery.isFetching,
                      onPageChange: setLifeRecordsPage,
                    })}
                  </div>
                </div>

                {lifeRecordsQuery.data?.setupPending ? (
                  <div className="mt-6 rounded-2xl border border-dashed border-amber-300 bg-amber-50 p-5 text-sm leading-7 text-amber-900">
                    {lifeRecordsQuery.data?.setupMessage}
                  </div>
                ) : isLifeScopeLoading || lifeRecordsQuery.isLoading ? (
                  <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm leading-7 text-slate-600">
                    Carregando vidas do contexto atual...
                  </div>
                ) : !lifeManagementHasScope ? (
                  <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm leading-7 text-slate-600">
                    Nenhuma empresa foi identificada no escopo autorizado deste
                    usuário.
                  </div>
                ) : visibleLifeRecords.length === 0 ? (
                  <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm leading-7 text-slate-600">
                    Nenhuma vida encontrada para os filtros aplicados nesta consulta.
                  </div>
                ) : (
                  <div className="mt-6">
                    {selectedLifeRecord ? (
                      <div className="space-y-4">
                        <div className="rounded-[1.8rem] border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="space-y-3">
                              <Button
                                variant="outline"
                                className="w-fit"
                                onClick={() => setSelectedLifeRecordId(null)}
                              >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Voltar para a lista
                              </Button>
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  Visualização dedicada da vida
                                </p>
                                <h4 className="mt-2 text-2xl font-semibold text-slate-950">
                                  {selectedLifeRecord.fullName ||
                                    "Vida sem nome"}
                                </h4>
                                <p className="mt-2 text-sm leading-6 text-slate-600">
                                  Consulte todos os dados cadastrais, o vínculo
                                  familiar, a situação operacional e o
                                  rastreamento técnico desta vida sem perder os
                                  filtros aplicados na listagem.
                                </p>
                                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-600">
                                  <span>
                                    <strong>CPF:</strong>{" "}
                                    {selectedLifeRecord.cpfNormalized || "-"}
                                  </span>
                                  <span>
                                    <strong>Tipo:</strong>{" "}
                                    {getLifeBaseBeneficiaryTypeLabel(
                                      selectedLifeRecord.beneficiaryType
                                    )}
                                  </span>
                                  {selectedLifeRecord.beneficiaryType ===
                                  "dependente" ? (
                                    <span>
                                      <strong>CPF do titular:</strong>{" "}
                                      {selectedLifeRecord.holderCpfNormalized ||
                                        "-"}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge
                                variant="outline"
                                className={cn(
                                  getConsolidatedStatusBadgeClass(
                                    selectedLifeRecord.consolidatedStatus ??
                                      selectedLifeRecord.status
                                  )
                                )}
                              >
                                {selectedLifeRecord.consolidatedStatusLabel ||
                                  "Status não informado"}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={cn(
                                  getPendingStatusBadgeClass(
                                    selectedLifeRecord.pendingStatusLabel ||
                                      "Sem pendência"
                                  )
                                )}
                              >
                                {selectedLifeRecord.pendingStatusLabel ||
                                  "Sem pendência operacional"}
                              </Badge>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-[1.8rem] border border-slate-200 bg-white shadow-sm">
                          <div className="border-b border-slate-200 px-6 py-5">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  Ficha completa da vida
                                </p>
                                <p className="mt-2 text-sm leading-6 text-slate-600">
                                  A visualização abaixo preserva todos os campos
                                  já disponibilizados pelo módulo atual.
                                </p>
                              </div>
                              {selectedLifeRecord.planName ? (
                                <Badge variant="outline">
                                  {selectedLifeRecord.planName}
                                </Badge>
                              ) : null}
                            </div>
                          </div>

                          <div className="grid gap-4 px-6 py-6 xl:grid-cols-2">
                            <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                Cadastro essencial
                              </p>
                              <div className="mt-3 grid gap-3 md:grid-cols-2">
                                <p>
                                  <strong>CPF:</strong>{" "}
                                  {selectedLifeRecord.cpfNormalized || "-"}
                                </p>
                                <p>
                                  <strong>Tipo de beneficiário:</strong>{" "}
                                  {getLifeBaseBeneficiaryTypeLabel(
                                    selectedLifeRecord.beneficiaryType
                                  )}
                                </p>
                                <p>
                                  <strong>Data de nascimento:</strong>{" "}
                                  {formatLifeBaseDate(
                                    selectedLifeRecord.birthDate
                                  )}
                                </p>
                                <p>
                                  <strong>Sexo:</strong>{" "}
                                  {selectedLifeRecord.gender || "-"}
                                </p>
                                <p>
                                  <strong>E-mail:</strong>{" "}
                                  {selectedLifeRecord.email || "-"}
                                </p>
                                <p>
                                  <strong>Telefone:</strong>{" "}
                                  {selectedLifeRecord.phone || "-"}
                                </p>
                                <p>
                                  <strong>Empresa:</strong>{" "}
                                  {selectedLifeRecord.companyName ||
                                    operationalCompany?.nomeFantasia ||
                                    operationalCompany?.razaoSocial ||
                                    "-"}
                                </p>
                                <p>
                                  <strong>Plano:</strong>{" "}
                                  {selectedLifeRecord.planName || "-"}
                                </p>
                              </div>
                            </div>

                            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5">
                              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                Vínculo familiar
                              </p>
                              <div className="mt-3 grid gap-3 md:grid-cols-2">
                                <p>
                                  <strong>Grau de parentesco:</strong>{" "}
                                  {getLifeBaseRelationshipLabel(
                                    selectedLifeRecord.relationshipLabel ||
                                      selectedLifeRecord.relationshipType
                                  )}
                                </p>
                                {selectedLifeRecord.beneficiaryType ===
                                "dependente" ? (
                                  <>
                                    <p>
                                      <strong>CPF do titular:</strong>{" "}
                                      {selectedLifeRecord.holderCpfNormalized ||
                                        "-"}
                                    </p>
                                    <p>
                                      <strong>Nome do titular:</strong>{" "}
                                      {selectedLifeHolderName}
                                    </p>
                                  </>
                                ) : (
                                  <p className="md:col-span-2">
                                    <strong>Estrutura familiar:</strong> esta
                                    vida é o titular de referência do grupo.
                                  </p>
                                )}
                                <p className="md:col-span-2">
                                  <strong>Chave do grupo familiar:</strong>{" "}
                                  {selectedLifeRecord.familyGroupKey || "-"}
                                </p>
                              </div>
                            </div>

                            <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                Situação operacional
                              </p>
                              <div className="mt-3 space-y-3 text-sm leading-6 text-slate-700">
                                <p>
                                  <strong>Status consolidado:</strong>{" "}
                                  {selectedLifeRecord.consolidatedStatusLabel ||
                                    "-"}
                                </p>
                                <p>
                                  <strong>Status pendente:</strong>{" "}
                                  {selectedLifeRecord.pendingStatusLabel ||
                                    "Nenhuma movimentação pendente"}
                                </p>
                                <p>
                                  <strong>Status do provedor:</strong>{" "}
                                  {selectedLifeRecord.pendingProviderStatus ||
                                    "-"}
                                </p>
                                <p>
                                  <strong>Mensagem operacional:</strong>{" "}
                                  {selectedLifeRecord.pendingMessage ||
                                    "Sem movimentação pendente em aberto."}
                                </p>
                                <p>
                                  <strong>Lote relacionado:</strong>{" "}
                                  {selectedLifeRecord.pendingJobId
                                    ? `#${selectedLifeRecord.pendingJobId}`
                                    : "-"}
                                </p>
                              </div>
                            </div>

                            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5">
                              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                Faturamento
                              </p>
                              <div className="mt-3 grid gap-3 md:grid-cols-2 text-sm leading-6 text-slate-700">
                                <p>
                                  <strong>Data de inclusão para faturamento:</strong>{" "}
                                  {formatLifeBaseDate(
                                    selectedLifeRecord.billingInclusionDate
                                  )}
                                </p>
                                <p>
                                  <strong>Data de ativação:</strong>{" "}
                                  {formatDateTime(
                                    selectedLifeRecord.billingActivatedAt
                                  )}
                                </p>
                                <p className="md:col-span-2">
                                  <strong>Data de inativação:</strong>{" "}
                                  {formatDateTime(
                                    selectedLifeRecord.billingInactivatedAt
                                  )}
                                </p>
                              </div>
                            </div>

                            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5">
                              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                Rastreamento técnico
                              </p>
                              <div className="mt-3 grid gap-3 md:grid-cols-2">
                                <p>
                                  <strong>Criado em:</strong>{" "}
                                  {formatDateTime(selectedLifeRecord.createdAt)}
                                </p>
                                <p>
                                  <strong>Última atualização:</strong>{" "}
                                  {formatDateTime(selectedLifeRecord.updatedAt)}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 border-t border-slate-200 px-6 py-4">
                            {selectedLifeRecord.pendingJobId ? (
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setSelectedDraftId(
                                    Number(selectedLifeRecord.pendingJobId)
                                  );
                                  setLocation(
                                    "/portal/base-de-pessoas/importacao"
                                  );
                                }}
                              >
                                Abrir lote relacionado
                              </Button>
                            ) : null}
                            <Button
                              variant="outline"
                              onClick={() => setSelectedLifeRecordId(null)}
                            >
                              Voltar para a lista
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
                          Clique em uma vida para abrir a visualização dedicada
                          sem perder o contexto de busca e filtros já aplicados
                          na lista operacional.
                        </div>

                        <div className="space-y-3">
                          {visibleLifeRecords.map((record: any) => {
                            const consolidatedStatus =
                              record.consolidatedStatus ?? record.status;

                            return (
                              <button
                                key={record.id}
                                type="button"
                                onClick={() => setSelectedLifeRecordId(record.id)}
                                className="w-full rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 text-left transition hover:border-[#0b3b6f]/30 hover:bg-white hover:shadow-sm"
                              >
                                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                  <div className="min-w-0 flex-1 space-y-3">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <p className="text-base font-semibold text-slate-950">
                                        {record.fullName || "Vida sem nome"}
                                      </p>
                                      <Badge variant="outline">
                                        {getLifeBaseBeneficiaryTypeLabel(
                                          record.beneficiaryType
                                        )}
                                      </Badge>
                                      <Badge
                                        variant="outline"
                                        className={cn(
                                          getConsolidatedStatusBadgeClass(
                                            consolidatedStatus
                                          )
                                        )}
                                      >
                                        {record.consolidatedStatusLabel ||
                                          (consolidatedStatus === "active"
                                            ? "Ativa"
                                            : consolidatedStatus === "inactive"
                                              ? "Inativa"
                                              : "Não consolidada no portal")}
                                      </Badge>
                                      <Badge
                                        variant="outline"
                                        className={cn(
                                          getPendingStatusBadgeClass(
                                            record.pendingStatusLabel ||
                                              "Sem pendência"
                                          )
                                        )}
                                      >
                                        {record.pendingStatusLabel ||
                                          "Sem pendência operacional"}
                                      </Badge>
                                    </div>

                                    <div className="grid gap-2 text-sm text-slate-600 sm:grid-cols-2 xl:grid-cols-4">
                                      <span>
                                        <strong>CPF:</strong>{" "}
                                        {record.cpfNormalized || "-"}
                                      </span>
                                      <span>
                                        <strong>Tipo de beneficiário:</strong>{" "}
                                        {getLifeBaseBeneficiaryTypeLabel(
                                          record.beneficiaryType
                                        )}
                                      </span>
                                      <span>
                                        <strong>Status consolidado:</strong>{" "}
                                        {record.consolidatedStatusLabel ||
                                          (consolidatedStatus === "active"
                                            ? "Ativa"
                                            : consolidatedStatus === "inactive"
                                              ? "Inativa"
                                              : "Não consolidada no portal")}
                                      </span>
                                      <span>
                                        <strong>Status pendente:</strong>{" "}
                                        {record.pendingStatusLabel ||
                                          "Sem pendência operacional"}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="flex shrink-0 items-center">
                                    <span className="inline-flex items-center rounded-full border border-[#0b3b6f]/15 bg-white px-3 py-1 text-sm font-medium text-[#0b3b6f] shadow-sm">
                                      Ver ficha completa
                                    </span>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>

                        <div className="flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-sm text-muted-foreground">
                            Página <span className="font-medium text-slate-700">{lifeRecordsCurrentPage}</span>{" "}
                            de <span className="font-medium text-slate-700">{Math.max(lifeRecordsTotalPages, 1)}</span>{" "}
                            — exibindo <span className="font-medium text-slate-700">{lifeRecordsPageStart > 0 ? `${lifeRecordsPageStart}-${lifeRecordsPageEnd}` : "0"}</span>{" "}
                            de <span className="font-medium text-slate-700">{lifeRecordsTotalCount}</span> vida(s)
                          </p>
                          {renderLifeRecordsPagination({
                            currentPage: lifeRecordsCurrentPage,
                            totalPages: lifeRecordsTotalPages,
                            isFetching: lifeRecordsQuery.isFetching,
                            onPageChange: setLifeRecordsPage,
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </div>
          </section>
        ) : selectedSubview === "historico" ? (
          <LifeImportHistoryPanel
            scopedCompanies={scopedCompanies}
            isAdminRole={isAdminRole}
          />
        ) : (
          <LifeBaseImportWizard
            key={importWizardSessionKey}
            companySearch={companySearch}
            setCompanySearch={setCompanySearch}
            selectedCompanyId={selectedCompanyId}
            setSelectedCompanyId={setSelectedCompanyId}
            selectedTemplateType={selectedTemplateType}
            setSelectedTemplateType={setSelectedTemplateType}
            selectedImportFile={selectedImportFile}
            setSelectedImportFile={setSelectedImportFile}
            previewResult={previewResult}
            previewError={previewError}
            draftResult={draftResult}
            confirmResult={confirmResult}
            processResult={processResult}
            currentUserRole={currentUserRole}
            isAdminRole={isAdminRole}
            isSimpleOnlyExperience={isSimpleOnlyExperience}
            canUseMultiCompanyExperience={canUseMultiCompanyExperience}
            isMultiCompanyJourney={isMultiCompanyJourney}
            isScopeWideMultiCompanyJourney={isScopeWideMultiCompanyJourney}
            isAdminSelectableMultiCompanyJourney={isAdminSelectableMultiCompanyJourney}
            activeContextCompanyId={activeContextCompanyId}
            activeTemplate={activeTemplate}
            templateContract={templateContract}
            templateOptions={templateOptions}
            visibleTemplateFields={visibleTemplateFields}
            visibleCompanies={visibleCompanies}
            scopedCompanies={scopedCompanies}
            scopeEligibleCompanies={scopeEligibleCompanies}
            scopeCompaniesMissingIntegration={scopeCompaniesMissingIntegration}
            hasEligibleContract={hasEligibleContract}
            integrationConfigured={integrationConfigured}
            activeAllowedPlans={activeAllowedPlans}
            operationalCompany={operationalCompany}
            rootScopedCompany={rootScopedCompany}
            contextCompanyLabel={contextCompanyLabel}
            recentDrafts={recentDrafts}
            selectedDraftId={selectedDraftId}
            setSelectedDraftId={setSelectedDraftId}
            selectedDraft={selectedDraft}
            selectedOperationalTracking={selectedOperationalTracking}
            draftRows={draftRows}
            draftEvents={draftEvents}
            canManageGovernance={canManageGovernance}
            onDownloadWorkbook={downloadWorkbook}
            onPreviewUpload={handlePreviewUpload}
            onCreateDraft={handleCreateDraft}
            onConfirmDraft={handleConfirmDraft}
            onProcessDraft={handleProcessDraft}
            onRetryStuckDraft={handleRetryStuckDraft}
            onDiscardDraft={handleDiscardDraft}
            onRefreshDraftStatus={jobId => {
              if (effectiveCompanyIdForDraft) {
                refreshExternalStatusMutation.mutate({
                  companyId: effectiveCompanyIdForDraft,
                  jobId,
                });
              }
            }}
            previewImportPending={previewImportMutation.isPending}
            createDraftPending={createDraftMutation.isPending}
            confirmDraftPending={confirmDraftMutation.isPending}
            processDraftPending={processConfirmedBatchMutation.isPending}
            retryStuckDraftPending={retryStuckBatchMutation.isPending}
            discardDraftPending={discardDraftMutation.isPending}
            refreshDraftPending={refreshExternalStatusMutation.isPending}
            formatDateTime={formatDateTime}
            getDraftStatusLabel={getDraftStatusLabel}
            getOperationalStatusTone={getOperationalStatusTone}
            getInspectedLifeBaseRowDetails={getInspectedLifeBaseRowDetails}
          />
        )}
      </div>
    </SalviaLayout>
  );
}
